/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useEnhancedEffect } from "./useEnhancedEffect";
import { isSSR } from "../utils/isSSR";
import { useLatest } from "./useLatest";
import throttle from "lodash.throttle";

export type UseResizeObserverCallback = (entry: ResizeObserverEntry, observer: () => void) => void;

function createResizeObserver() {
	const callbacks: Map<unknown, Array<UseResizeObserverCallback>> = new Map();
	const observer = new ResizeObserver(
		throttle((entries, obs) => {
			for (let i = 0; i < entries.length; i++) {
				const cbs = callbacks.get(entries[i].target);
				cbs?.forEach((cb) => cb(entries[i], obs));
			}
		})
	);

	return {
		observer,
		subscribe(target: HTMLElement, callback: UseResizeObserverCallback) {
			observer.observe(target);
			const cbs = callbacks.get(target) ?? [];
			cbs.push(callback);
			callbacks.set(target, cbs);
		},
		unsubscribe(target: HTMLElement, callback: UseResizeObserverCallback) {
			observer.unobserve(target);
			const cbs = callbacks.get(target) ?? [];
			if (cbs.length === 1) {
				callbacks.delete(target);
				return;
			}
			const cbIndex = cbs.indexOf(callback);
			if (cbIndex !== -1) cbs.splice(cbIndex, 1);
			callbacks.set(target, cbs);
		}
	};
}

let _resizeObserver: ReturnType<typeof createResizeObserver>;

const getResizeObserver = () =>
	!_resizeObserver ? (_resizeObserver = createResizeObserver()) : _resizeObserver;

export function useResizeObserver<T extends HTMLElement>(
	target: React.MutableRefObject<T> | T,
	callback: UseResizeObserverCallback
): void {
	const resizeObserver = getResizeObserver();
	const storedCallback = useLatest(callback);

	useEnhancedEffect(() => {
		if (isSSR()) {
			return;
		}

		const targetEl = target && "current" in target ? target.current : target;

		if (!targetEl) {
			return;
		}

		let didUnsubscribe = false;

		function cb(entry: ResizeObserverEntry, observer: () => void) {
			if (didUnsubscribe) {
				return;
			}

			storedCallback.current(entry, observer);
		}

		resizeObserver.subscribe(targetEl as HTMLElement, cb);

		return () => {
			didUnsubscribe = true;
			resizeObserver.unsubscribe(targetEl as HTMLElement, cb);
		};
	}, [ target, resizeObserver, storedCallback ]);
}
